import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { ensureProfile } from '@/lib/ensureProfile'
import { useAuth } from '@/contexts/AuthContext'
import { format } from 'date-fns'
import { useGroupsFeedShowVendorGroupsFlag } from '@/hooks/useGroupsFeedShowVendorGroupsFlag'

interface JoinGroupModalProps {
  onClose: () => void
  onSuccess: () => void
}

interface AvailableGroup {
  id: string
  name: string
  description: string | null
  owner_id: string
  created_at: string
  owner: string
  profiles: {
    display_name: string
  } | null
}

export default function JoinGroupModal({ onClose, onSuccess }: JoinGroupModalProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Focus search input on mount
  useEffect(() => {
    searchInputRef.current?.focus()
  }, [])

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const { showVendorGroupsInFeed } = useGroupsFeedShowVendorGroupsFlag()

  // Fetch groups user is NOT a member of (optionally exclude vendor groups per feature flag)
  const { data: availableGroups, isLoading } = useQuery({
    queryKey: ['available-groups', user?.id, showVendorGroupsInFeed],
    queryFn: async () => {
      if (!user) return []

      const { data: memberships } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', user.id)

      const userGroupIds = memberships?.map((m) => m.group_id) || []

      const { data: allGroups, error } = await supabase
        .from('groups')
        .select(`
          id,
          name,
          description,
          owner_id,
          group_type,
          created_at,
          profiles!groups_owner_id_fkey (
            display_name
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error

      const isVendor = (g: any) => g.group_type === 'broadcaster' || g.group_type === 'ad-creator'
      return (allGroups || [])
        .filter((g: any) => {
          if (userGroupIds.includes(g.id)) return false
          if (!showVendorGroupsInFeed && isVendor(g)) return false
          return true
        })
        .map((g: any) => ({
          ...g,
          owner: g.profiles?.display_name || 'Unknown',
        })) as AvailableGroup[]
    },
    enabled: !!user,
  })

  // Join group mutation
  const joinGroup = useMutation({
    mutationFn: async (groupId: string) => {
      if (!user) throw new Error('Not authenticated')
      await ensureProfile(user)

      const { error } = await supabase
        .from('group_members')
        .insert({
          group_id: groupId,
          user_id: user.id,
          role: 'member',
        })

      if (error) throw error
    },
    onSuccess: () => {
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['user-groups', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['my-groups', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['other-groups', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['user-groups-sidebar', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['available-groups', user?.id] })
      onSuccess()
    },
  })

  const handleJoinGroup = async (groupId: string, groupName: string) => {
    if (confirm(`Join "${groupName}"?`)) {
      try {
        await joinGroup.mutateAsync(groupId)
      } catch (err: any) {
        alert(err.message || 'Failed to join group')
      }
    }
  }

  // Filter groups based on search
  const filteredGroups = availableGroups?.filter((group) => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      group.name.toLowerCase().includes(query) ||
      group.description?.toLowerCase().includes(query) ||
      (group.profiles?.display_name || '').toLowerCase().includes(query)
    )
  }) || []

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="join-group-title"
    >
      <div
        className="rounded-lg p-6 max-w-2xl w-full max-h-[80vh] flex flex-col"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '2px solid var(--bg-tertiary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 
            id="join-group-title"
            className="text-2xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            Join a Group
          </h2>
          <button
            onClick={onClose}
            className="p-2"
            aria-label="Close dialog"
            style={{ color: 'var(--text-primary)' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Search Input */}
        <div className="mb-4">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
            aria-label="Search groups"
          />
        </div>

        {/* Groups List */}
        <div className="flex-1 overflow-y-auto mb-4">
          {isLoading ? (
            <p style={{ color: 'var(--text-primary)' }}>Loading groups...</p>
          ) : filteredGroups.length === 0 ? (
            <p style={{ color: 'var(--text-primary)' }}>
              {searchQuery.trim() 
                ? 'No groups found matching your search.' 
                : 'No groups available to join.'}
            </p>
          ) : (
            <div className="space-y-3">
              {filteredGroups.map((group) => {
                const createdDate = format(new Date(group.created_at), 'dd/MM/yyyy')
                
                return (
                  <div
                    key={group.id}
                    className="p-4 rounded border-2"
                    style={{
                      backgroundColor: 'var(--bg-primary)',
                      borderColor: 'var(--bg-tertiary)',
                    }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 
                          className="font-semibold text-lg mb-1"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {group.name}
                        </h3>
                        {group.description && (
                          <p 
                            className="text-sm mb-2"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {group.description}
                          </p>
                        )}
                        <div className="text-sm space-y-1">
                          <p style={{ color: 'var(--text-primary)' }}>
                            Owner: {group.owner}
                          </p>
                          <p style={{ color: 'var(--text-primary)' }}>
                            Created: {createdDate}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleJoinGroup(group.id, group.name)}
                        disabled={joinGroup.isPending}
                        className="px-4 py-2 font-semibold rounded whitespace-nowrap disabled:opacity-50"
                        style={{
                          backgroundColor: 'var(--bg-interactive)',
                          color: 'var(--text-inverse)',
                        }}
                        aria-label={`Join ${group.name}`}
                      >
                        {joinGroup.isPending ? 'Joining...' : 'Join'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Close Button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

